import type { EnvironmentalResponse, UtilityInference } from "@shared/api";
import type { PropertySummary } from "@shared/api";
import type { SolarPotentialReport } from "../../score/computeSolarPotential";
import type {
  ExternalContextReportData,
  LotAnalysisReportData,
  ParcelIntelReportData,
  SunViewsReportData
} from "../createPropertyReport";
import type { CameraPreset } from "../../scene/createCamera";
import type { StructureEarthwork } from "../../state/computeStructureEarthwork";
import {
  defaultStructuresState,
  defaultVisibility,
  type LayerVisibility,
  type ViewState
} from "../../state/layerState";
import type { PlacedStructure } from "../../state/structuresState";
import type { MountInspectorRailsOptions } from "./mountInspectorRails";
import type { StructureBoundsFt } from "./StructuresCard";
import type { BuildabilityScore } from "../../score/computeBuildabilityScore";
import type { PropertySummaryStatus } from "./PropertyReportMetricsCard";

/** Options bag passed to {@link mountInspectorRails} excluding mount-only props. */
export type InspectorRailHandlers = Omit<MountInspectorRailsOptions, "container">;

/** Read-only accessors for right-rail "Details" inspector cards — mirrors mount* getters. */
export interface InspectorDrawerDetailsControls {
  getBuildabilityScore: () => BuildabilityScore | null;
  getSummary: () => PropertySummary | null;
  getSummaryStatus: () => PropertySummaryStatus;
  getParcelIntel: () => ParcelIntelReportData | null;
  getSiteCenter: () => { lat: number; lon: number };
  getLot: () => LotAnalysisReportData | null;
  getSunViewsReport: () => SunViewsReportData;
  getExternalReport: () => ExternalContextReportData | null;
  /**
   * Phase 3 environmental envelope (EPA / USFS wildfire / USDA soil /
   * HUD FMR / AirNow AQI). Drives the env-sourced rows on the mobile
   * drawer's EnvironmentalRiskCard. Null until `/v1/environmental`
   * resolves; the card renders `—` for env rows in the meantime.
   */
  getEnvironmental: () => EnvironmentalResponse | null;
  onCameraPreset: (preset: CameraPreset) => void;
}

/** Imperative glue between {@link bootViewer}'s closures and SPA drawer UI (mobile). */
export interface InspectorDrawerControls {
  getViewState(): ViewState;
  /**
   * Pushes visibility to the THREE scene immediately without mutating
   * `state.visibility`. Used by the handheld drawer staging flow so sliders
   * feel live; {@link commitLayerVisibility} persists + runs full rebuilds.
   */
  previewLayerVisibility(visibility: LayerVisibility): void;
  /**
   * Merges `visibility` onto the viewer's persisted map and runs the usual
   * `onLayerVisibilityChange` rebuild path (+ inspector bumps).
   */
  commitLayerVisibility(visibility: LayerVisibility): void;
  railHandlers: InspectorRailHandlers;
  details: InspectorDrawerDetailsControls;
}

/** Valid placeholder so drawer drill-ins never read `undefined` before `bootViewer` registers. */
function noopViewState(): ViewState {
  return {
    visibility: defaultVisibility(),
    sun: {
      dateTime: new Date(0),
      shadowsEnabled: false
    },
    views: {
      viewpoint: null,
      eyeHeightFt: 8
    },
    structures: defaultStructuresState()
  };
}

const noopHandlers: InspectorRailHandlers = {
  getReportToken: () => null,
  getIllustrationsVersion: () => 0,
  getViewState: () => noopViewState(),
  onIllustrationsBegin() {},
  onBaseVisibilityToggle() {},
  onTerrainVisibilityToggle() {},
  onSunVisibilityToggle() {},
  onSunMonthDayHourChange() {},
  onSunPreset() {},
  getSolar(): SolarPotentialReport | null {
    return null;
  },
  onViewsVisibilityToggle() {},
  onViewsEyeHeightLive() {},
  onViewsEyeHeightRebuild() {},
  onViewsPickViewpoint() {},
  onViewsResetViewpoint() {},
  onFloodVisibilityToggle() {},
  onFloodRefreshExternalData() {},
  getUtilityInferences(): UtilityInference[] | null {
    return null;
  },
  onUtilitiesOverlayToggle() {},
  getStructureBoundsFt(): StructureBoundsFt {
    return { minXFt: 0, maxXFt: 0, minYFt: 0, maxYFt: 0 };
  },
  getStructuresEditable: () => true,
  onStructuresBeginDraw() {},
  onStructuresAutoPlace() {},
  getCanAutoPlace(): boolean {
    return false;
  },
  getAutoPlacementReasoning(): string | null {
    return null;
  },
  onStructureUpdate() {},
  onStructureDelete() {},
  onStructureSectionToggle() {},
  getEarthworkForStructure(): StructureEarthwork | null {
    return null;
  }
};

const noopDetails: InspectorDrawerDetailsControls = {
  getBuildabilityScore: () => null,
  getSummary: () => null,
  getSummaryStatus: () => "unavailable",
  getParcelIntel: () => null,
  getSiteCenter: () => ({ lat: 0, lon: 0 }),
  getLot: () => null,
  getSunViewsReport: () =>
    ({
      dateTime: new Date(0),
      sunrise: new Date(0),
      solarNoon: new Date(0),
      sunset: new Date(0),
      daylightHours: 0,
      bestBuildableSunFraction: null,
      viewshed: null,
      bestViewOffset: null
    }) satisfies SunViewsReportData,
  getExternalReport: () => null,
  getEnvironmental: () => null,
  onCameraPreset() {}
};

const noopControls: InspectorDrawerControls = {
  getViewState: () => noopViewState(),
  previewLayerVisibility() {},
  commitLayerVisibility() {},
  railHandlers: noopHandlers,
  details: noopDetails
};

const listeners = new Set<() => void>();

let active: InspectorDrawerControls | null = null;

export function subscribeInspectorDrawerControls(cb: () => void): () => void {
  listeners.add(cb);
  // Resync callers that subscribed after registration already pinged idle listeners,
  // and pick up bridge attachment mid-boot without stale memoized handles.
  cb();
  return () => {
    listeners.delete(cb);
  };
}

export function notifyInspectorDrawerControlsSubscribers(): void {
  for (const l of listeners) {
    l();
  }
}

export function registerInspectorDrawerControls(
  next: InspectorDrawerControls | null
): void {
  active = next;
  notifyInspectorDrawerControlsSubscribers();
}

export function getInspectorDrawerControls(): InspectorDrawerControls {
  return active ?? noopControls;
}
